// Diff prev/next state and dispatch the right Supabase mutations.
// Best-effort: errors are toasted, local state is the source of truth for UI.

import {
  upsertAppointment, deleteAppointment,
  upsertPet, deletePet,
  upsertService, deleteService,
  upsertAddon, deleteAddon,
  upsertInventory, deleteInventory,
  patchProfile, patchTenant,
  patchCustomerMembership,
  pushNotification, markNotificationRead,
  insertReview, logAudit,
  upsertPlatformConfig,
  insertAnnouncement, deleteAnnouncement,
  syncAppointmentToGoogle,
} from "./api";

const eq = (a, b) => {
  if (a === b) return true;
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
};

// Returns { added: [], removed: [], changed: [] } for arrays keyed by `id`
// (exported for the native save-watchdog's verify step in useAppState.js)
export function diffArrayById(prev = [], next = []) {
  const prevMap = new Map(prev.map(x => [x.id, x]));
  const nextMap = new Map(next.map(x => [x.id, x]));
  const added = [], changed = [], removed = [];
  for (const [id, item] of nextMap) {
    if (!prevMap.has(id)) added.push(item);
    else if (!eq(prevMap.get(id), item)) changed.push(item);
  }
  for (const [id, item] of prevMap) {
    if (!nextMap.has(id)) removed.push(item);
  }
  return { added, removed, changed };
}

// For object dictionaries (users{}, pets{})
// (exported for the native save-watchdog's verify step in useAppState.js)
export function diffDict(prev = {}, next = {}) {
  const added = [], changed = [], removed = [];
  for (const [id, item] of Object.entries(next)) {
    if (!prev[id]) added.push(item);
    else if (!eq(prev[id], item)) changed.push({ id, ...item, _prev: prev[id] });
  }
  for (const [id, item] of Object.entries(prev)) {
    if (!next[id]) removed.push(item);
  }
  return { added, removed, changed };
}

// For tenants array nested fields (services, addons, inventory, etc.)
function diffNestedArray(prevTenants = [], nextTenants = [], field) {
  const out = { added: [], removed: [], changed: [] };
  for (const nt of nextTenants) {
    const pt = prevTenants.find(t => t.id === nt.id);
    const prev = pt?.[field] || [];
    const next = nt[field] || [];
    const d = diffArrayById(prev, next);
    out.added.push(...d.added.map(x => ({ ...x, tenantId: nt.id })));
    out.changed.push(...d.changed.map(x => ({ ...x, tenantId: nt.id })));
    out.removed.push(...d.removed.map(x => ({ ...x, tenantId: nt.id })));
  }
  return out;
}

// Yield to the event loop so the UI thread can paint/handle input between
// heavy phases. Prefers the modern scheduler.yield() where available, falls
// back to MessageChannel (faster than setTimeout(0)), then setTimeout.
const yieldToMain = () => {
  if (typeof globalThis === "undefined") return Promise.resolve();
  const s = globalThis.scheduler;
  if (s && typeof s.yield === "function") return s.yield();
  if (typeof MessageChannel === "function") {
    return new Promise((resolve) => {
      const mc = new MessageChannel();
      mc.port1.onmessage = () => resolve();
      mc.port2.postMessage(0);
    });
  }
  return new Promise((r) => setTimeout(r, 0));
};

export async function syncStateDiff(prevState, nextState, options = {}) {
  if (!prevState || !nextState) return;
  const { byUser } = options;
  const errors = [];
  const safe = async (label, fn) => { try { await fn(); } catch (e) { errors.push(label + ": " + (e.message || e)); } };

  // ── Pets (must run BEFORE appointments because appointments.pet_id FK
  //   references pets.id; a new walk-in / customer booking inserts pet +
  //   appointment in one setState, and the appointment would fail FK
  //   validation if it raced ahead). ────────────────────────────────────────
  const pets = diffDict(prevState.pets || {}, nextState.pets || {});
  for (const p of pets.added)   await safe("pet.add",    () => upsertPet(p));
  for (const p of pets.changed) await safe("pet.update", () => upsertPet(p));

  // ── Appointments ─────────────────────────────────────────────────────────
  const appt = diffArrayById(prevState.appointments || [], nextState.appointments || []);
  const failedAptIds = new Set();
  for (const a of appt.added) {
    let ok = false;
    try { await upsertAppointment(a); ok = true; }
    catch (e) { errors.push("apt.add: " + (e.message || e)); }
    if (!ok) failedAptIds.add(a.id);
    else syncAppointmentToGoogle(a.id, a.staffId);
  }
  for (const a of appt.changed) {
    await safe("apt.update", () => upsertAppointment(a));
    // Fire-and-forget GCal sync. Pass BOTH the new staff_id and
    // (if it changed) the previous one so the edge function deletes
    // the event from the prior staff's calendar.
    const prev = (prevState.appointments || []).find(x => x.id === a.id);
    syncAppointmentToGoogle(a.id, a.staffId || prev?.staffId);
    if (byUser) {
      // audit_log RLS only permits is_staff_of(tenant_id) inserts. For
      // customer-initiated changes (reschedule, cancel, rating) this will
      // RLS-reject; swallow rather than surfacing "儲存失敗" — the appointment
      // itself is already persisted and audit is best-effort anyway.
      const prev = (prevState.appointments || []).find(x => x.id === a.id);
      // NOT awaited. The audit trail is best-effort by design (it RLS-rejects
      // outright for customer-initiated changes), so it must never sit between
      // the owner and a finished save — a slow audit insert used to hold the
      // whole sync chain open and keep 正在儲存… on screen long after the
      // booking itself had committed.
      logAudit({
        tenantId: a.tenantId, userId: byUser, action: "appointment.update",
        entityType: "appointment", entityId: a.id, before: prev, after: a,
      }).catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("[PettoBook sync] apt.audit skipped:", e?.message || e);
      });
    }
  }
  for (const a of appt.removed) {
    await safe("apt.delete", () => deleteAppointment(a.id));
    syncAppointmentToGoogle(a.id, a.staffId);
  }

  // Pet deletes deferred until after appointment deletes so we don't violate
  // the FK in the other direction (deleting a pet that still has appointments).
  for (const p of pets.removed) await safe("pet.delete", () => deletePet(p.id));
  await yieldToMain();

  // ── Users (profiles) ─────────────────────────────────────────────────────
  const users = diffDict(prevState.users || {}, nextState.users || {});
  // We DON'T create users via direct table insert (auth.users requires signup flow).
  // We DO patch existing profiles, but with a narrow allow-list so we don't
  // trip the financial / sensitive guards. Direct save sites (profile editor,
  // owner staff editor) call patchProfile themselves with the exact fields
  // they want to change; this background diff only handles bulk drift.
  for (const u of users.changed) {
    // Only send columns that aren't trigger-guarded. Financial columns
    // (wallet, points, walletTx, rating, reviewCount, baseSalary, etc) are
    // owner / system writes — never client-side bulk sync.
    const safeUpdate = {
      name: u.name, phone: u.phone, countryCode: u.countryCode,
      phone2: u.phone2, phone2CountryCode: u.phone2CountryCode,
      address: u.address, avatar: u.avatar, enabled: u.enabled,
      schedulePreference: u.schedulePreference,
      workDays: u.workDays, staffHours: u.staffHours,
      // jobType / wageLog deliberately NOT here (2026-09-28). They're pay
      // inputs: the owner's staff editor, 每日入數 and payouts save them with
      // patchProfile directly. Sending them from a bulk diff let a device with
      // a stale copy overwrite wages the owner had just entered, and since the
      // payroll guard locks them on self-edit, a staff member's own name/phone
      // save would now fail outright if their copy had drifted.
      tags: u.tags, referredBy: u.referredBy,
      notes: u.notes, mustChangePassword: u.mustChangePassword,
    };
    await safe("profile.update", () => patchProfile(u.id, safeUpdate));
  }

  // ── Tenants ──────────────────────────────────────────────────────────────
  const ten = diffArrayById(prevState.tenants || [], nextState.tenants || []);
  for (const t of ten.changed) await safe("tenant.update", () => patchTenant(t.id, t));
  await yieldToMain();

  // ── Services / Addons / Inventory (nested in tenants[]) ──────────────────
  const svcDiff = diffNestedArray(prevState.tenants, nextState.tenants, "services");
  for (const s of svcDiff.added)   await safe("svc.add",    () => upsertService(s));
  for (const s of svcDiff.changed) await safe("svc.update", () => upsertService(s));
  for (const s of svcDiff.removed) await safe("svc.delete", () => deleteService(s.id));

  const adDiff = diffNestedArray(prevState.tenants, nextState.tenants, "addons");
  for (const a of adDiff.added)   await safe("addon.add",    () => upsertAddon(a));
  for (const a of adDiff.changed) await safe("addon.update", () => upsertAddon(a));
  for (const a of adDiff.removed) await safe("addon.delete", () => deleteAddon(a.id));

  const invDiff = diffNestedArray(prevState.tenants, nextState.tenants, "inventory");
  for (const i of invDiff.added)   await safe("inv.add",    () => upsertInventory(i));
  for (const i of invDiff.changed) await safe("inv.update", () => upsertInventory(i));
  for (const i of invDiff.removed) await safe("inv.delete", () => deleteInventory(i.id));
  await yieldToMain();

  // ── Notifications ────────────────────────────────────────────────────────
  const notif = diffArrayById(prevState.notifications || [], nextState.notifications || []);
  for (const n of notif.added) {
    if (String(n.id || "").startsWith("local-")) continue;
    // Skip notifications whose appointment didn't make it to DB this cycle.
    if (n.aptId && failedAptIds.has(n.aptId)) continue;
    // Notifications are best-effort: the in-app UI already shows them
    // from local state, the DB row is just for cross-device sync, and the
    // owner-side copy is fanned out by the notify_owner_on_appointment
    // trigger. Phase 1.4 — instead of swallowing failures silently, push
    // a `[notif-soft]` tagged entry into the errors[] array. The UI
    // downgrades these to a quieter "(部分通知未送達，但資料已儲存)" toast
    // instead of the loud "儲存失敗" header. Owner stays informed without
    // alarm.
    try { await pushNotification(n); } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[PettoBook sync] notif.add skipped:", e?.message || e);
      errors.push("[notif-soft] " + (e?.message || e));
    }
  }
  for (const n of notif.changed) {
    if (n.read) await safe("notif.read", () => markNotificationRead(n.id));
  }
  // notifications removed locally are ignored (client-side dismiss)

  // ── Reviews ──────────────────────────────────────────────────────────────
  const rev = diffArrayById(prevState.reviews || [], nextState.reviews || []);
  for (const r of rev.added) await safe("review.add", () => insertReview(r));

  // ── Announcements (superadmin only via RLS) ──────────────────────────────
  const ann = diffArrayById(prevState.announcements || [], nextState.announcements || []);
  for (const a of ann.added)   await safe("ann.add",    () => insertAnnouncement(a));
  for (const a of ann.removed) await safe("ann.delete", () => deleteAnnouncement(a.id));

  // ── Customer memberships (wallet / points / wallet_tx) ───────────────────
  // The customer-facing booking flow deducts wallet + points on
  // submitBooking. These fields live on customer_memberships (per shop), so
  // updating profiles.points would not affect anything; we patch the right
  // row here. Key on (userId, tenantId). Skipped entries are silently OK.
  // Membership diff — DROPPED wallet/points/walletTx/vouchers. The
  // authoritative writers for those are now SECURITY DEFINER RPCs
  // (customer_settle_booking, customer_refund_cancellation,
  //  redeem_package_atomic, restore_package_visit_atomic,
  //  award_loyalty_for_apt) and direct owner-side
  //  patchCustomerMembership calls. Including them in the diff caused
  // last-write-wins races where the debounced sync would overwrite
  // server-canonical balances with a stale local snapshot.
  // What remains: display-name / phone / tags only.
  const prevMems = prevState.memberships || [];
  const nextMems = nextState.memberships || [];
  const keyMem = m => (m.userId + "|" + m.tenantId);
  const prevMemMap = new Map(prevMems.map(m => [keyMem(m), m]));
  for (const nm of nextMems) {
    const pm = prevMemMap.get(keyMem(nm));
    if (!pm) continue; // self-upsert is done via RPC elsewhere
    const labelChanged =
         pm.displayName !== nm.displayName
      || pm.phone       !== nm.phone
      || !eq(pm.tags, nm.tags);
    if (!labelChanged) continue;
    await safe("mem.patch", () => patchCustomerMembership({
      userId: nm.userId,
      tenantId: nm.tenantId,
      tags: nm.tags,
      displayName: nm.displayName,
      phone: nm.phone,
    }));
  }

  // ── Platform config + auto-notifications (singleton row) ─────────────────
  // RLS: only superadmin can write to platform_config. Non-superadmin edits
  // will fail safely (logged in errors, local state remains as user typed).
  const platChanged = !eq(prevState.platformConfig || {}, nextState.platformConfig || {});
  const autoChanged = !eq(prevState.autoNotifications || [], nextState.autoNotifications || []);
  if (platChanged || autoChanged) {
    await safe("platform_config.upsert", () => upsertPlatformConfig({
      config: nextState.platformConfig || {},
      autoNotifications: nextState.autoNotifications || [],
    }));
  }

  if (errors.length > 0) {
    // eslint-disable-next-line no-console
    console.warn("[PettoBook sync] partial errors:", errors);
  }
  return { errors };
}
